import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useLocations } from '../../hooks/useLocations';
import { useToast } from '../../components/ui/Toast';
import { fine, weight as fmtWeight } from '../../utils/format';
import { Drawer } from '../../components/ui/Form';
import { PhotoCell } from '../../components/ui/PhotoCell';
import { ErrorNotice } from '../../components/ui';

export interface Piece {
  id: string;
  item_code: string;
  barcode: string | null;
  status: string;
  product_name: string;
  purity_code: string;
  branch_id: string;
  branch_name: string;
  gross_wt: string;
  net_wt: string;
  fine_wt: string;
  image_data_uri?: string | null;
  has_own_photo?: boolean;
  location_id?: string | null;
  location_name?: string | null;
}

interface Move {
  moved_at: string;
  from_location: string | null;
  to_location: string | null;
  reason: string | null;
  moved_by: string | null;
}

/**
 * One piece: its photo, where it is, and everywhere it has been.
 *
 * This is where a piece that cannot be found gets traced — the move history
 * says who put it where, and when — and where a piece that arrived without a
 * photo finally gets one.
 */
export function PieceDrawer({ piece, onClose }: { piece: Piece; onClose: () => void }) {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const { locations } = useLocations(piece.branch_id);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);

  const moves = useQuery({
    queryKey: ['moves', piece.id],
    queryFn: () => api.get<{ data: Move[] }>(`/items/${piece.id}/moves`),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['items'] });
    void client.invalidateQueries({ queryKey: ['moves', piece.id] });
  };

  const move = useMutation({
    mutationFn: () =>
      api.post<{ from: string | null; to: string | null }>(`/items/${piece.id}/location`, {
        locationId: target || null,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (r) => {
      toast(`${piece.item_code} moved`, `${r.from ?? 'not placed'} → ${r.to ?? 'not placed'}`);
      setTarget('');
      setReason('');
      setError(null);
      refresh();
    },
    onError: setError,
  });

  const photo = useMutation({
    mutationFn: (uri: string | null) => api.put(`/items/${piece.id}/photo`, { imageDataUri: uri }),
    onSuccess: () => {
      toast('Photo saved');
      refresh();
    },
    onError: setError,
  });

  const onPremises = ['IN_STOCK', 'RESERVED', 'ON_APPROVAL', 'RETURNED'].includes(piece.status);
  const history = moves.data?.data ?? [];

  return (
    <Drawer title={piece.item_code} subtitle={`${piece.product_name} · ${piece.purity_code}`} onClose={onClose}>
      <ErrorNotice error={error} />

      <div className="space-y-5">
        <section className="flex gap-4">
          <div className="h-40 w-40 shrink-0 overflow-hidden rounded-lg border border-silver-100 bg-silver-50">
            {piece.image_data_uri ? (
              <img src={piece.image_data_uri} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="grid h-full w-full place-items-center text-2xs text-ink-faint">No photo yet</span>
            )}
          </div>
          <div className="space-y-1 text-sm">
            <p>
              <span className="text-ink-soft">Gross </span>
              <span className="num">{fmtWeight(piece.gross_wt)}</span>
            </p>
            <p>
              <span className="text-ink-soft">Net </span>
              <span className="num">{fmtWeight(piece.net_wt)}</span>
            </p>
            <p>
              <span className="text-ink-soft">Fine </span>
              <span className="num">{fine(piece.fine_wt)}</span>
            </p>
            <p>
              <span className="text-ink-soft">Barcode </span>
              <span className="num">{piece.barcode ?? '— use the tag number'}</span>
            </p>
            {piece.image_data_uri && !piece.has_own_photo ? (
              <p className="text-2xs text-warn">Showing the product photo — this piece has none of its own.</p>
            ) : null}
            {can('item.create') ? (
              <div className="flex items-center gap-2 pt-2">
                <PhotoCell value={null} onChange={(uri) => uri && photo.mutate(uri)} size="h-10 w-10" />
                <span className="text-2xs text-ink-soft">
                  {piece.has_own_photo ? 'Replace this piece’s photo' : 'Take a photo of this piece'}
                </span>
              </div>
            ) : null}
          </div>
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold">Where it is</h3>
          <p className="mb-2 text-sm">
            {piece.location_name ? (
              <span className="rounded bg-gold-50 px-2 py-0.5 font-medium text-gold-700">{piece.location_name}</span>
            ) : (
              <span className="text-warn">Not placed anywhere</span>
            )}
            <span className="ml-2 text-2xs text-ink-faint">{piece.branch_name}</span>
          </p>

          {can('stock.move_location') && onPremises ? (
            <div className="space-y-2 rounded-md border border-silver-100 bg-surface p-3">
              <div className="flex gap-2">
                <select className="field" value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">Move to…</option>
                  {locations
                    .filter((l) => l.id !== piece.location_id)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                </select>
                <button className="btn-primary whitespace-nowrap" disabled={!target || move.isPending} onClick={() => move.mutate()}>
                  Move
                </button>
              </div>
              <input
                className="field"
                placeholder="Why (optional) — e.g. night lock-up, customer viewing"
                value={reason}
                maxLength={200}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          ) : !onPremises ? (
            <p className="text-2xs text-ink-faint">
              {piece.status.replace(/_/g, ' ').toLowerCase()} — not in the shop to move.
            </p>
          ) : null}
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold">Where it has been</h3>
          {history.length === 0 ? (
            <p className="text-sm text-ink-soft">No moves recorded.</p>
          ) : (
            <ol className="space-y-1">
              {history.map((m, i) => (
                <li key={i} className="flex items-baseline gap-2 border-b border-silver-100/70 py-1.5 text-sm last:border-0">
                  <span className="num w-32 shrink-0 text-2xs text-ink-faint">
                    {new Date(m.moved_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span>
                    {m.from_location ?? 'arrived'} → <span className="font-medium">{m.to_location ?? 'not placed'}</span>
                    {m.reason ? <span className="block text-2xs text-ink-soft">{m.reason}</span> : null}
                  </span>
                  <span className="ml-auto text-2xs text-ink-faint">{m.moved_by}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </Drawer>
  );
}
